import { useEffect, useRef } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useInstitutionAccess } from "@/shared/hooks/useInstitutionAccess";
import { useAuth } from "@/shared/hooks/useAuth";
import { Button } from "@/shared/components/ui/button";

/**
 * Guard para rotas /acesso-externo/*.
 * Usuários institution_external: checagem de cadastro ativo.
 * Demais autenticados (equipe do escritório): acesso permitido — escopo por RPC/RLS (ex.: instituicao_por_id).
 */
export default function ExternalUserGuard() {
  const { institutionId: guardInstId } = useParams<{ institutionId?: string }>();
  const { isExternalUser, portalAccessAllowed, loading } = useInstitutionAccess(guardInstId);
  const { signOut } = useAuth();
  /** Já houve verificação concluída que permitia renderizar o Outlet — mantém rotas montadas se `loading` voltar a true. */
  const hadSuccessfulAccessCheckRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!isExternalUser) {
      hadSuccessfulAccessCheckRef.current = true;
      return;
    }
    hadSuccessfulAccessCheckRef.current = portalAccessAllowed;
  }, [loading, isExternalUser, portalAccessAllowed]);

  if (loading && hadSuccessfulAccessCheckRef.current) {
    return <Outlet />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f9f6f0] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#791614] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground font-body">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  /* Equipe do escritório acessa o mesmo portal em nova aba (sem novo login); o RPC instituicao_por_id restringe o que cada um vê. */
  if (!isExternalUser) {
    return <Outlet />;
  }

  if (!portalAccessAllowed) {
    return (
      <div className="min-h-screen bg-[#f9f6f0] flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4 font-body">
          <h1 className="font-display text-xl text-[#791614]">Cadastro inativo</h1>
          <p className="text-sm text-muted-foreground">
            Seu acesso ao portal da instituição está desativado. Entre em contato com o escritório.
          </p>
          <Button type="button" variant="outline" className="font-body" onClick={() => void signOut()}>
            Sair
          </Button>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
